import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import { SocketProvider } from './hooks/useSocket';
import { IaJobsProvider } from './context/IaJobsProvider';
import { OnboardingPackProvider } from './context/OnboardingPackProvider';
import AppShell from './shell/AppShell';
import AuthGuard from './shell/AuthGuard';
import ErrorBoundary from './components/ErrorBoundary';

const LandingPage = lazy(() => import('./pages/Root'));
const LoginPage = lazy(() => import('./pages/Login'));
const SignupProfilePage = lazy(() => import('./pages/SignupProfile'));
const SignupAccountPage = lazy(() => import('./pages/SignupAccount'));
const SignupSuccessPage = lazy(() => import('./pages/SignupSuccess'));
const SignupCancelPage = lazy(() => import('./pages/SignupCancel'));
const SignupResumePage = lazy(() => import('./pages/SignupResume'));
const TermsPage = lazy(() => import('./pages/Terms'));
const PrivacyPage = lazy(() => import('./pages/Privacy'));
const HomePage = lazy(() => import('./pages/Home'));
const SessionPage = lazy(() => import('./pages/Session'));
const PlanPage = lazy(() => import('./pages/Plan'));
const HistoryPage = lazy(() => import('./pages/History'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const DuoPage = lazy(() => import('./pages/Duo'));
const WorkoutsManagePage = lazy(() => import('./pages/WorkoutsManage'));
const StatisticsPage = lazy(() => import('./pages/Statistics'));
const ExerciseVideoLibraryPage = lazy(() => import('./pages/ExerciseVideoLibrary'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'));

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--ludi-bdr-m)] border-t-ludi-emp" />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SocketProvider>
          <IaJobsProvider>
            <OnboardingPackProvider>
              <AppShell>
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/start" element={<SignupProfilePage />} />
                    <Route path="/register" element={<SignupAccountPage />} />
                    <Route path="/register/success" element={<SignupSuccessPage />} />
                    <Route path="/register/cancel" element={<SignupCancelPage />} />
                    <Route path="/register/resume" element={<SignupResumePage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />

                    <Route path="/workout" element={<Navigate to="/session" replace />} />

                    <Route element={<AuthGuard />}>
                      <Route path="/home" element={<HomePage />} />
                      <Route path="/session" element={<SessionPage />} />
                      <Route path="/plan" element={<PlanPage />} />
                      <Route path="/history" element={<HistoryPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/duo" element={<DuoPage />} />
                      <Route path="/workouts-manage" element={<WorkoutsManagePage />} />
                      <Route path="/videos" element={<ExerciseVideoLibraryPage />} />
                      <Route path="/statistics" element={<StatisticsPage />} />
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </AppShell>
            </OnboardingPackProvider>
          </IaJobsProvider>
        </SocketProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
